import { closestCenter, DndContext, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { IconCirclePlus, IconEdit, IconInfoCircleFilled, IconLoader2 } from '@tabler/icons-react';
import { AxiosError } from 'axios';
import useTheme from 'chat-ui/src/hooks/useTheme';
import { useEffect, useMemo, useRef, useState } from 'react';
import { shuffleArray } from '@common/utils';
import { toast } from 'react-toastify';
import { v4 as uuidv4 } from 'uuid';
import { useMutateDeleteCategories, useMutateGenerateCategories } from '@/reactQuery/post';
import useTranslation from '@/hooks/useTranslation';
import { ECategoryMode, EStatusCode } from '@/enums';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import useCategoryStore from '@/store/CategoryStore';
import { CategoryData, EditCategoriesModalProps } from '@/types';
import categoriesColorList from '@/utils/categoriesColorList';
import FooterButtonsCategory from './EditOrganizationModal/FooterButtonsCategory';
import InfoScreenCategory from './EditOrganizationModal/InfoScreenCategory';
import { ExpandableText } from '../expandableText/ExpandableText';
import CategoryEditItem from '../QuestionItem/CategoryEditItem';
import TooltipWrapper from '../tooltip/TooltipWrapper';

const MAX_CATEGORIES = 30;

const ERRORS = {
  AT_LEAST_2_ELEMENTS: 400,
};

function EditCategoriesModal({
  data,
  projectId,
  questionId,
  questionText,
  setOpenModal,
  openModal,
  isCategoryDataExists,
  conversationsNumber,
  isLoading,
}: EditCategoriesModalProps) {
  const { lang } = useTranslation();
  const scrollRef = useRef<HTMLDivElement>(null);

  const categoryDataWithRequired = useMemo(
    () => data?.map((item) => ({ ...item, required: false })) || [],
    [data],
  );

  const [category, setCategory] = useState<CategoryData[]>(categoryDataWithRequired);
  const [disableSubmit, setDisableSubmit] = useState(true);
  const { currentTheme } = useTheme();
  const [openInfoScreen, setOpenInfoScreen] = useState(false);
  const [openRemoveScreen, setOpenRemoveScreen] = useState(false);
  const categoryType = useCategoryStore((state) => state.categoryType);
  const setCategoryType = useCategoryStore((state) => state.setCategoryType);

  const { mutateAsync, status } = useMutateGenerateCategories();
  const { mutateAsync: mutateAsyncDeleteCategories, status: statusDeleteCategories } =
    useMutateDeleteCategories();

  const isCategoryModeSingle = categoryType === ECategoryMode.SINGLE;
  const isCategoryModeMultiple = categoryType === ECategoryMode.MULTIPLE;

  const handleChangeCategoryMode = (choosenCategory: ECategoryMode | null) => {
    setCategoryType(choosenCategory);
  };

  const handleInfoScreen = () => {
    setOpenInfoScreen(!openInfoScreen);
    setOpenRemoveScreen(false);
  };

  const handleRemoveScreen = () => {
    setOpenRemoveScreen(!openRemoveScreen);
    setOpenInfoScreen(false);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active?.id !== over?.id) {
      setCategory((items) => {
        const activeIndex = items.findIndex((q) => q?.id === active?.id);
        const overIndex = items.findIndex((q) => q?.id === over?.id);

        const reorderedItems = arrayMove(items, activeIndex, overIndex);

        return reorderedItems;
      });
    }
  };

  const scrollToBottom = () => {
    if (scrollRef?.current) {
      scrollRef.current.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  };

  const addQuestion = () => {
    if (category?.length >= MAX_CATEGORIES) {
      toast.info(lang.get('msg.maxNumberOfCategories'));
      return;
    }
    const existingColors = category.map((item) => item.color);
    const availableColors = categoriesColorList.filter((color) => !existingColors.includes(color));
    const color = shuffleArray(availableColors)[0] || categoriesColorList[0];

    const newCategory = {
      id: uuidv4(),
      color,
      label: '',
      prompt: '',
      required: false,
    };

    setCategory((prevCategory) => {
      const newCategories = [...(prevCategory || []), newCategory];
      setTimeout(scrollToBottom, 0); // defer scroll until after DOM updates
      return newCategories;
    });
  };

  const handleDeleteQuestion = (idToDelete: string) => {
    setCategory((prevCategory) => prevCategory.filter((item) => item.id !== idToDelete));
  };

  const handleUpdateCategory = (id: string, field: string, value: string) => {
    setCategory((prevCategory) =>
      prevCategory.map((item) => (item.id === id ? { ...item, [field]: value } : item)),
    );
  };

  const handleEditCategories = async () => {
    try {
      await mutateAsync({
        projectId,
        formData: category,
        categoryType,
        questionId,
        conversationsNumber,
      });
      setOpenModal(false);
      toast.success(lang.get('msg.categoriesSuccessfullyUpdated'));
    } catch (err) {
      const axiosError = err as AxiosError;
      if (axiosError?.status === EStatusCode.LABELS_MUST_BE_UNIQUE) {
        toast.error(lang.get('msg.labelsMustBeUnique'));
      } else if (axiosError?.status === ERRORS.AT_LEAST_2_ELEMENTS) {
        toast.error(lang.get('msg.categoriesMustContainAtLeastTwoCategories'));
      } else {
        toast.error(lang.get('msg.errorPleaseTryAgain'));
      }
    }
  };

  const handleDeleteCategories = async () => {
    try {
      await mutateAsyncDeleteCategories({ projectId, questionId, categoryType });
      toast.success(lang.get('msg.categoriesSuccessfullyRemoved'));
      setOpenModal(false);
      setOpenRemoveScreen(false);
      setOpenInfoScreen(false);
    } catch {
      toast.error(lang.get('msg.errorPleaseTryAgain'));
    }
  };

  useEffect(() => {
    const isEmpty = category.some((item) => !item.label.trim() || !item.prompt.trim());
    setDisableSubmit(isEmpty);
  }, [category]);

  useEffect(() => {
    setCategory(categoryDataWithRequired);
  }, [categoryDataWithRequired]);

  return (
    <AlertDialog open={openModal}>
      {isCategoryDataExists && (
        <TooltipWrapper text={lang.get('msg.editCategories')} className="mb-1">
          <AlertDialogTrigger asChild>
            <Button onClick={() => setOpenModal(true)} variant="ghost" className="p-2">
              <IconEdit size={22} />
            </Button>
          </AlertDialogTrigger>
        </TooltipWrapper>
      )}
      <AlertDialogContent className="max-w-xl max-h-[98%] overflow-y-scroll overflow-x-hidden">
        {!openInfoScreen && !openRemoveScreen && (
          <>
            <AlertDialogHeader>
              <AlertDialogTitle>{lang.get('msg.editCategories')}</AlertDialogTitle>
            </AlertDialogHeader>

            <div className="flex flex-col">
              <div className="flex justify-between w-full mb-4">
                <div className="flex flex-col gap-2">
                  <p className="mb-1 text-sm font-semibold">{lang.get('msg.categoryMode')}:</p>
                  <div className="flex items-center gap-2">
                    <Button
                      disabled={
                        isLoading || status === 'pending' || statusDeleteCategories === 'pending'
                      }
                      onClick={() => handleChangeCategoryMode(ECategoryMode.SINGLE)}
                      style={{
                        backgroundColor: isCategoryModeSingle
                          ? currentTheme?.primary
                          : 'transparent',
                        borderColor: currentTheme?.primary,
                        color: isCategoryModeSingle ? '#FFFFFF' : currentTheme?.primary,
                      }}
                      variant="default"
                      className="px-5 font-semibold border-[3px] rounded-full h-7"
                    >
                      {lang.get('msg.single')}
                    </Button>
                    <Button
                      disabled={
                        isLoading || status === 'pending' || statusDeleteCategories === 'pending'
                      }
                      onClick={() => handleChangeCategoryMode(ECategoryMode.MULTIPLE)}
                      style={{
                        backgroundColor: isCategoryModeMultiple
                          ? currentTheme?.primary
                          : 'transparent',
                        borderColor: currentTheme?.primary,
                        color: isCategoryModeMultiple ? '#FFFFFF' : currentTheme?.primary,
                      }}
                      variant="default"
                      className="px-5 font-semibold border-[3px] rounded-full h-7"
                    >
                      {lang.get('msg.multiple')}
                    </Button>
                  </div>
                </div>
                <TooltipWrapper text={lang.get('msg.clickForMoreInfo')} className="mr-24">
                  <button
                    onClick={handleInfoScreen}
                    style={{ color: currentTheme?.primary }}
                    type="button"
                    className="self-start"
                  >
                    <IconInfoCircleFilled size={24} />
                  </button>
                </TooltipWrapper>
              </div>

              {questionText && (
                <div className="text-sm text-muted-foreground pb-2">
                  <p className="font-semibold mb-1">{lang.get('msg.question')}:</p>
                  <ExpandableText
                    text={questionText}
                    className="inline-block items-center pr-1"
                    maxLines={2}
                  />
                </div>
              )}

              <div className="flex flex-col items-center justify-center w-full">
                {/* categories exists and data is not loading */}
                {category?.length !== 0 && !isLoading && !!categoryType && (
                  <div
                    ref={scrollRef}
                    className="flex flex-col overflow-x-hidden w-full h-full max-h-[360px] pb-3 pt-1 gap-5 pr-3 overflow-y-scroll"
                  >
                    <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                      <SortableContext items={category} strategy={verticalListSortingStrategy}>
                        {category?.map((item, index) => (
                          <CategoryEditItem
                            key={item?.id}
                            index={index}
                            color={item?.color}
                            prompt={item?.prompt}
                            label={item?.label}
                            id={item?.id}
                            required={item?.required}
                            handleDeleteQuestion={handleDeleteQuestion}
                            handleUpdateCategory={handleUpdateCategory}
                            status={status}
                          />
                        ))}
                      </SortableContext>
                    </DndContext>
                  </div>
                )}

                {/* data is loading */}
                {isLoading && (
                  <div className="flex items-center justify-center w-full h-full py-10">
                    <IconLoader2
                      style={{ color: currentTheme?.primary }}
                      size={40}
                      className="animate-spin"
                    />
                  </div>
                )}

                {/* data is not loading and categories does not exist */}
                {!isLoading && category?.length === 0 && !categoryType && (
                  <p className="py-3 text-sm">There is no categories added</p>
                )}

                {/* if categories number is below max number then show button */}
                {category?.length < MAX_CATEGORIES && (
                  <Button
                    style={{ backgroundColor: currentTheme?.primary }}
                    onClick={addQuestion}
                    className="self-end w-full px-3 mt-8 font-semibold text-white"
                    variant="secondary"
                    disabled={status === 'pending'}
                  >
                    <IconCirclePlus size={18} className="mt-px mr-2" />
                    {lang.get('msg.addCategory')}
                  </Button>
                )}
              </div>
            </div>
          </>
        )}

        {/* categories info screen */}
        {openInfoScreen && <InfoScreenCategory />}

        {/* remove categories screen */}
        {openRemoveScreen && (
          <div className="flex flex-col items-center justify-center p-5">
            <h3 className="text-lg font-semibold">{lang.get('msg.removingCategories')}</h3>
            <p className="mt-1 font-medium">
              {lang.get('msg.areYouSureYouWantToRemoveCategories')}
            </p>
          </div>
        )}

        {/* footer buttons */}
        <FooterButtonsCategory
          status={status}
          handleEditCategories={handleEditCategories}
          handleRemoveScreen={handleRemoveScreen}
          data={data}
          disableSubmit={disableSubmit}
          handleInfoScreen={handleInfoScreen}
          openInfoScreen={openInfoScreen}
          openRemoveScreen={openRemoveScreen}
          setCategory={setCategory}
          setOpenModal={setOpenModal}
          handleDeleteCategories={handleDeleteCategories}
          statusDeleteCategories={statusDeleteCategories}
        />
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default EditCategoriesModal;
